import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as api from "../api";
import {
  PlatformEventExtended,
  CreatePlatformEventPayload,
} from "../types";

// Query keys
export const platformEventKeys = {
  all: ["platformEvents"] as const,
  lists: () => [...platformEventKeys.all, "list"] as const,
  details: () => [...platformEventKeys.all, "detail"] as const,
  detail: (id: string) => [...platformEventKeys.details(), id] as const,
};

/**
 * Hook to fetch a single platform event by ID
 */
export function usePlatformEventQuery(id: string) {
  return useQuery<{ data: PlatformEventExtended }>({
    queryKey: platformEventKeys.detail(id),
    queryFn: () => api.getPlatformEvent(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a new platform event (for frontend tracking)
 */
export function useCreatePlatformEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreatePlatformEventPayload) => api.createPlatformEvent(payload),
    onSuccess: () => {
      // Invalidate all event lists to refresh data
      queryClient.invalidateQueries({ queryKey: platformEventKeys.lists() });
    },
  });
}

/**
 * Utility hook to track an event without waiting for result
 * Useful for fire-and-forget analytics events
 */
export function useTrackEvent() {
  const { mutate } = useCreatePlatformEvent();

  return (eventName: string, options?: Omit<CreatePlatformEventPayload, "eventName">) => {
    mutate({
      eventName,
      ...options,
    });
  };
}

export { default as usePlatformEventCable } from "./usePlatformEventCable";
